import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useLocation } from 'react-router-dom';
import Bluebird from 'bluebird';
import queryString from 'query-string';
import { Grid, Box, Container, Typography, FormHelperText, Tooltip, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import routes from '../../routes';
import * as authService from '../../services/auth';
import * as authApi from '../../api/auth';
import snackbarHelper from '../../helpers/snackbarHelper';
import ButtonWithLoading from '../../components/ButtonWithLoading';
import { useFormStyles } from './authStyles';
import { getPasswordsValidationErrors } from '../../helpers/validators';
import loginLogo from '../../assets/svg/new-logo-with-title-white.svg';
import { Colors } from '../../assets/theme/Colors';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import SocialMediaLinkIcons from '../../components/SocialMediaLinkIcons';
import RFFTextField from '../../components/Common/RFFTextField';

const ResetPasswordPage = () => {
  const authStyles = useFormStyles();
  const history = useHistory();
  const location = useLocation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // redirect to index if user is already logged in
  if (authService.getCurrentlyLoggedInUser()) {
    history.push(routes.index);
  }

  const parsedSearch = queryString.parse(location.search);
  const { token } = parsedSearch;

  const validateForm = values => getPasswordsValidationErrors(values, 'password1', 'password2');

  const onSubmit = values => {
    const { password1 } = values;

    setSubmitting(true);

    return Bluebird.try(() => authApi.resetPassword(token, password1))
      .then(res => res.json())
      .then(() => {
        setSubmitting(false);
        setShowDialog(true);
      })
      .catch(err => {
        setSubmitting(false);

        let errorMessage = err.message;

        if (err.code === 'ERR_INVALID_TOKEN') {
          errorMessage = 'Invalid token. Please use a valid one.';
        }

        if (err.code === 'ERR_TOKEN_ALREADY_USED') {
          errorMessage = 'This token has been already used.';
        }

        if (err.code === 'ERR_TOKEN_EXPIRED') {
          errorMessage = 'Token expired.';
        }

        if (err.code === 'ERR_USER_NOT_FOUND') {
          errorMessage = 'User not found.';
        }

        snackbarHelper.error(errorMessage);
      });
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <Box sx={authStyles.pageBackground}>
      <Container maxWidth="md">
        {showDialog && (
          <ConfirmationDialog
            message="Your new password has been set. You can now login using your new password."
            buttons={[{ label: 'Got it', onClick: () => history.push(routes.login) }]}
            closeDialog={() => setShowDialog(false)}
          />
        )}

        <Grid container spacing={2} sx={authStyles.pageContainer} data-testid="welcome-index-page">
          <Grid item xs sx={authStyles.logoWrapper}>
            <Box component="img" src={loginLogo} alt="logo" sx={authStyles.logo} />
          </Grid>
          <Grid item xs sx={authStyles.loginFormWrapper}>
            <Typography
              sx={{ typography: 'h5', color: `${Colors.white} !important`, textAlign: 'center', fontWeight: 600 }}
              data-testid="signup-header"
            >
              Password recovery
            </Typography>

            <Form
              onSubmit={onSubmit}
              validate={validateForm}
              render={({ handleSubmit, submitting, pristine, invalid, dirtySinceLastSubmit, form, values }) => (
                <form style={authStyles.form} onSubmit={handleSubmit}>
                  <Box sx={[{ marginBottom: 0 }, authStyles.input]}>
                    <RFFTextField
                      margin="normal"
                      name="password1"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      placeholder="Password"
                      autoComplete="new-password"
                      data-testid="register-password-input"
                      InputProps={{
                        endAdornment: (
                          <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword} size="large">
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </Box>

                  <FormHelperText style={authStyles.helpText}>
                    At least 8 characters including a number and an uppercase letter.
                  </FormHelperText>

                  <Box sx={authStyles.input}>
                    <RFFTextField
                      margin="normal"
                      name="password2"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      placeholder="Repeat password"
                      autoComplete="new-password"
                      data-testid="register-password-confirmation-input"
                      InputProps={{
                        endAdornment: (
                          <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword} size="large">
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </Box>

                  <ButtonWithLoading
                    sx={authStyles.submitButton}
                    fullWidth
                    type="submit"
                    isLoading={submitting || isSubmitting}
                    disabled={pristine || (invalid && !dirtySinceLastSubmit)}
                  >
                    Reset Password
                  </ButtonWithLoading>
                </form>
              )}
            />

            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ paddingTop: 2 }}>
              <SocialMediaLinkIcons iconColor="#fff" />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
